"use strict";

var validationUtils = (function() {
    var MIN_TIN_LENGTH = 1;
    var MAX_TIN_LENGTH = 20;
    var FIJI_TIN_LENGTH = 9;
    var MAX_PHONE_LENGTH = 15;
    var MIN_PHONE_LENGTH = 1;
    var MAX_NAME_LENGTH = 200;
    var MAX_EMAIL_LENGTH = 254;
    var MAX_COMMENTS_LENGTH = 2000;
    var MAX_REQUESTED_LIMIT = 1000000000;
    var MAX_FILENAME_LENGTH = 250;

    var DANGEROUS_EXTENSIONS = [
        'exe', 'dll', 'bat', 'cmd', 'ps1', 'vbs', 'js', 'jar', 'msi',
        'scr', 'pif', 'com', 'hta', 'cpl', 'msc', 'reg', 'ws', 'wsf',
        'wsh', 'lnk', 'inf', 'sh', 'bash', 'php', 'asp', 'aspx', 'jsp'
    ];

    var ALLOWED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
    var ALLOWED_MIME_TYPES = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    function isNotEmpty(value) {
        return value !== null && value !== undefined && String(value).trim() !== '';
    }

    function isValidTIN(tin) {
        if (!tin) return false;
        var cleanedTin = String(tin).replace(/[^0-9]/g, '');
        return cleanedTin.length >= MIN_TIN_LENGTH && cleanedTin.length <= MAX_TIN_LENGTH;
    }

    function isValidFijiTIN(tin) {
        if (!tin) return false;
        var cleanedTin = String(tin).replace(/[^0-9]/g, '');
        return cleanedTin.length === FIJI_TIN_LENGTH;
    }

    function isValidPhone(phone) {
        if (!phone) return false;
        var cleanedPhone = String(phone).replace(/[^0-9]/g, '');
        return cleanedPhone.length >= MIN_PHONE_LENGTH && cleanedPhone.length <= MAX_PHONE_LENGTH;
    }

    function isValidEmail(email) {
        if (!email) return false;
        email = String(email).trim();
        if (email.length > MAX_EMAIL_LENGTH) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    function isValidName(name) {
        if (!name) return false;
        name = String(name).trim();
        if (name.length > MAX_NAME_LENGTH) return false;
        if (name.length < 2) return false;
        return /^[\p{L}\s\-'.]+$/u.test(name);
    }

    function isValidNumber(value) {
        var num = parseFloat(value);
        return !isNaN(num) && isFinite(num);
    }

    function isValidPositiveAmount(value) {
        var num = parseFloat(value);
        return !isNaN(num) && isFinite(num) && num > 0;
    }

    function isValidRequestedLimit(value) {
        var num = parseFloat(value);
        return !isNaN(num) && isFinite(num) && num > 0 && num <= MAX_REQUESTED_LIMIT;
    }

    function isGreaterThan(value, min) {
        var numValue = parseFloat(value);
        return !isNaN(numValue) && numValue > min;
    }

    function isNotPastDate(dateString) {
        var inputDate = new Date(dateString);
        if (isNaN(inputDate.getTime())) {
            return false;
        }
        var today = new Date();
        today.setHours(0, 0, 0, 0);
        inputDate.setHours(0, 0, 0, 0);
        return inputDate >= today;
    }

    function isDateWithinRange(dateString, maxMonthsInFuture) {
        var inputDate = new Date(dateString);
        if (isNaN(inputDate.getTime())) {
            return false;
        }
        var today = new Date();
        today.setHours(0, 0, 0, 0);
        inputDate.setHours(0, 0, 0, 0);
        if (inputDate < today) {
            return false;
        }
        var maxDate = new Date();
        maxDate.setMonth(maxDate.getMonth() + maxMonthsInFuture);
        maxDate.setHours(0, 0, 0, 0);
        return inputDate <= maxDate;
    }

    function isValidFileSize(file, maxSizeInMB) {
        if (!file) return false;
        var maxSizeInBytes = maxSizeInMB * 1024 * 1024;
        return file.size > 0 && file.size <= maxSizeInBytes;
    }

    function isValidFileType(file, allowedTypes) {
        if (!file || !allowedTypes || allowedTypes.length === 0) return false;

        var fileName = file.name.toLowerCase();
        var fileExtension = '.' + fileName.split('.').pop();

        var extensionAllowed = allowedTypes.some(function(type) {
            if (type.startsWith('.')) {
                return fileExtension === type.toLowerCase();
            }
            if (type.endsWith('/*')) {
                var mainType = type.slice(0, -2);
                return file.type.startsWith(mainType + '/');
            }
            return file.type === type;
        });

        if (!extensionAllowed) return false;

        var mimeTypeAllowed = ALLOWED_MIME_TYPES.some(function(mime) {
            return file.type === mime;
        });

        return mimeTypeAllowed;
    }

    function isValidFilename(filename) {
        if (!filename) return false;
        if (filename.length > MAX_FILENAME_LENGTH) return false;

        if (/\.\./.test(filename) || /[\/\\]/.test(filename)) {
            return false;
        }

        if (/[\x00-\x1f\x7f]/.test(filename)) {
            return false;
        }

        var parts = filename.toLowerCase().split('.');
        if (parts.length > 2) {
            for (var i = 0; i < parts.length - 1; i++) {
                if (DANGEROUS_EXTENSIONS.indexOf(parts[i]) !== -1) {
                    return false;
                }
            }
        }

        var lastExtension = parts[parts.length - 1];
        if (DANGEROUS_EXTENSIONS.indexOf(lastExtension) !== -1) {
            return false;
        }

        var allowedExtension = ALLOWED_FILE_EXTENSIONS.some(function(ext) {
            return filename.toLowerCase().endsWith(ext);
        });

        return allowedExtension;
    }

    function isValidComments(text) {
        if (!text) return true;
        return String(text).length <= MAX_COMMENTS_LENGTH;
    }

    function encodeHtmlEntities(str) {
        if (!str) return '';
        var div = document.createElement('div');
        div.textContent = String(str);
        return div.innerHTML;
    }

    function sanitizeInput(input) {
        if (!input) return '';
        var sanitized = String(input)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#x27;')
            .replace(/\//g, '&#x2F;')
            .replace(/`/g, '&#x60;')
            .replace(/=/g, '&#x3D;')
            .trim();
        return sanitized;
    }

    function sanitizeForDisplay(input) {
        if (!input) return '';
        return encodeHtmlEntities(String(input).trim());
    }

    function sanitizeTIN(tin) {
        if (!tin) return '';
        return String(tin).replace(/[^0-9]/g, '').substring(0, MAX_TIN_LENGTH);
    }

    function sanitizePhone(phone) {
        if (!phone) return '';
        return String(phone).replace(/[^\d\+\-\s\(\)]/g, '').trim().substring(0, MAX_PHONE_LENGTH + 5);
    }

    function sanitizeEmail(email) {
        if (!email) return '';
        return String(email).trim().toLowerCase().substring(0, MAX_EMAIL_LENGTH);
    }

    function sanitizeName(name) {
        if (!name) return '';
        return String(name)
            .replace(/[^\p{L}\s\-'.]/gu, '')
            .trim()
            .substring(0, MAX_NAME_LENGTH);
    }

    function sanitizeFilename(filename) {
        if (!filename) return '';
        return String(filename)
            .replace(/[^\w\-. ]/g, '_')
            .replace(/\.{2,}/g, '.')
            .substring(0, MAX_FILENAME_LENGTH);
    }

    function showError(selector, message) {
        var $element = $(selector);
        $element.addClass('is-invalid');
        var errorContainer = $(selector + 'Error');
        var safeMessage = encodeHtmlEntities(message);
        if (errorContainer.length > 0) {
            errorContainer.text(message);
        } else {
            $element.next('.invalid-feedback').text(message);
        }
    }

    function clearError(selector) {
        var $element = $(selector);
        $element.removeClass('is-invalid');
        var errorContainer = $(selector + 'Error');
        if (errorContainer.length > 0) {
            errorContainer.text('');
        } else {
            $element.next('.invalid-feedback').text('');
        }
    }

    function showSuccess(selector) {
        $(selector).removeClass('is-invalid').addClass('is-valid');
    }

    function clearValidation(selector) {
        var $element = $(selector);
        $element.removeClass('is-invalid is-valid');
        var errorContainer = $(selector + 'Error');
        if (errorContainer.length > 0) {
            errorContainer.text('');
        } else {
            $element.next('.invalid-feedback').text('');
        }
    }

    function showAlert(message, type, selector) {
        selector = selector || '#alertMessage';
        var $alert = $(selector);
        $alert.text(message)
            .removeClass('alert-success alert-danger alert-warning alert-info')
            .addClass('alert-' + type)
            .show();
        setTimeout(function() {
            $alert.fadeOut();
        }, 5000);
    }

    function validateForm(formSelector, validations) {
        var isFormValid = true;
        validations.forEach(function(fieldValidation) {
            var fieldSelector = fieldValidation.field;
            var fieldValue = $(fieldSelector).val();
            fieldValue = fieldValue !== null && fieldValue !== undefined ? fieldValue.trim() : '';
            var isFieldValid = true;
            clearValidation(fieldSelector);
            for (var i = 0; i < fieldValidation.validations.length; i++) {
                var validation = fieldValidation.validations[i];
                if (!validation.isValid(fieldValue)) {
                    showError(fieldSelector, validation.message);
                    isFieldValid = false;
                    isFormValid = false;
                    break;
                }
            }
            if (isFieldValid && fieldValue) {
                showSuccess(fieldSelector);
            }
        });
        return isFormValid;
    }

    function createSafeElement(tag, textContent, className) {
        var element = document.createElement(tag);
        if (textContent) {
            element.textContent = textContent;
        }
        if (className) {
            element.className = className;
        }
        return element;
    }

    return {
        constants: {
            MIN_TIN_LENGTH: MIN_TIN_LENGTH,
            MAX_TIN_LENGTH: MAX_TIN_LENGTH,
            FIJI_TIN_LENGTH: FIJI_TIN_LENGTH,
            MAX_PHONE_LENGTH: MAX_PHONE_LENGTH,
            MAX_NAME_LENGTH: MAX_NAME_LENGTH,
            MAX_EMAIL_LENGTH: MAX_EMAIL_LENGTH,
            MAX_COMMENTS_LENGTH: MAX_COMMENTS_LENGTH,
            MAX_REQUESTED_LIMIT: MAX_REQUESTED_LIMIT,
            MAX_FILENAME_LENGTH: MAX_FILENAME_LENGTH,
            DANGEROUS_EXTENSIONS: DANGEROUS_EXTENSIONS,
            ALLOWED_FILE_EXTENSIONS: ALLOWED_FILE_EXTENSIONS,
            ALLOWED_MIME_TYPES: ALLOWED_MIME_TYPES
        },
        isNotEmpty: isNotEmpty,
        isValidTIN: isValidTIN,
        isValidFijiTIN: isValidFijiTIN,
        isValidPhone: isValidPhone,
        isValidEmail: isValidEmail,
        isValidName: isValidName,
        isValidNumber: isValidNumber,
        isValidPositiveAmount: isValidPositiveAmount,
        isValidRequestedLimit: isValidRequestedLimit,
        isGreaterThan: isGreaterThan,
        isNotPastDate: isNotPastDate,
        isDateWithinRange: isDateWithinRange,
        isValidFileSize: isValidFileSize,
        isValidFileType: isValidFileType,
        isValidFilename: isValidFilename,
        isValidComments: isValidComments,
        encodeHtmlEntities: encodeHtmlEntities,
        sanitizeInput: sanitizeInput,
        sanitizeForDisplay: sanitizeForDisplay,
        sanitizeTIN: sanitizeTIN,
        sanitizePhone: sanitizePhone,
        sanitizeEmail: sanitizeEmail,
        sanitizeName: sanitizeName,
        sanitizeFilename: sanitizeFilename,
        showError: showError,
        clearError: clearError,
        showSuccess: showSuccess,
        clearValidation: clearValidation,
        showAlert: showAlert,
        validateForm: validateForm,
        createSafeElement: createSafeElement
    };
})();
var applicationData = {
    applicantDetails: {
        fullName: '',
        tin: '',
        phone: '',
        email: ''
    },

    applicationDetails: {
        bank: '',
        purpose: '',
        currentLimit: 0,
        requestedNewLimit: '',
        date: '',
        destinationCountryId: '',
        destinationCountryIds: [],
        additionalComments: ''
    },

    purposeOptions: [],
    bankOptions: [],

    initializeDefaults: function() {
        var defaultLimitElement = document.getElementById('defaultVisaDebitLimit');
        if (defaultLimitElement && defaultLimitElement.value) {
            var limit = parseFloat(defaultLimitElement.value);
            if (!isNaN(limit) && limit > 0) {
                this.applicationDetails.currentLimit = limit;
            }
        }
    },

    loadPurposeOptions: function(callback) {
        var self = this;
        axios.get(getApiUrl('/api/Application/CardPurposes'), {
            headers: getRequestHeaders()
        })
            .then(function(response) {
                if (response.data && Array.isArray(response.data)) {
                    self.purposeOptions = response.data.map(function(item) {
                        return {
                            value: item.value || item.id,
                            text: item.text || item.name
                        };
                    });

                    if (typeof callback === 'function') {
                        callback(true);
                    }
                }
            })
            .catch(function(error) {
                self.purposeOptions = [
                    { value: 'Travel', text: 'Travel' },
                    { value: 'OnlinePurchase', text: 'Online Purchase' },
                    { value: 'BusinessExpenses', text: 'Business Expenses' },
                    { value: 'Education', text: 'Education' },
                    { value: 'MedicalExpenses', text: 'Medical Expenses' },
                    { value: 'Other', text: 'Other' }
                ];

                if (typeof callback === 'function') {
                    callback(false, error);
                }
            });
    },

    loadBankOptions: function(callback) {
        var self = this;
        axios.get(getApiUrl('/api/Application/Banks'), {
            headers: getRequestHeaders()
        })
            .then(function(response) {
                if (response.data && Array.isArray(response.data)) {
                    self.bankOptions = response.data.map(function(item) {
                        return {
                            value: item.value || item.id,
                            text: item.text || item.name
                        };
                    });

                    if (typeof callback === 'function') {
                        callback(true);
                    }
                }
            })
            .catch(function(error) {
                self.bankOptions = [
                    { value: 'ANZ', text: 'ANZ' },
                    { value: 'BSP', text: 'BSP' },
                    { value: 'HFC', text: 'HFC Bank' },
                    { value: 'BRED', text: 'BRED Bank' },
                    { value: 'BOB', text: 'Bank of Baroda' }
                ];

                if (typeof callback === 'function') {
                    callback(false, error);
                }
            });
    },

    documentTypesByPurpose: {},
    documentRequirementsLoaded: false,

    loadDocumentTypesByPurpose: function(callback) {
        var self = this;
        axios.get(getApiUrl('/api/Document/Types/AllPurposeMappings'), {
            headers: getRequestHeaders()
        })
            .then(function(response) {
                if (response.data) {
                    self.documentTypesByPurpose = response.data;
                    self.documentRequirementsLoaded = true;

                    if (typeof callback === 'function') {
                        callback(true);
                    }
                }
            })
            .catch(function(error) {
                if (typeof callback === 'function') {
                    callback(false, error);
                }
            });
    },

    getDocumentTypes: function(purpose) {
        if (this.documentRequirementsLoaded && purpose in this.documentTypesByPurpose) {
            return this.documentTypesByPurpose[purpose];
        }
        return [];
    },

    getDocumentRequirements: function(purpose) {
        if (this.documentRequirementsLoaded && purpose in this.documentTypesByPurpose) {
            return this.documentTypesByPurpose[purpose].map(function(docType) {
                return docType.name + (docType.required ? ' (Required)' : ' (Optional)');
            });
        }

        switch(purpose) {
            case 'Travel':
                return [
                    'Valid passport with minimum 6 months validity',
                    'Travel itinerary or confirmed ticket',
                    'Accommodation booking confirmation'
                ];
            case 'OnlinePurchase':
                return [
                    'Invoice or quote from the online merchant',
                    'Description of items being purchased',
                    'Proof of online merchants legitimacy (if available)'
                ];
            case 'BusinessExpenses':
                return [
                    'Business registration certificate',
                    'Invoice or quote related to business expense',
                    'Letter explaining business purpose'
                ];
            case 'Education':
                return [
                    'Offer letter from educational institution',
                    'Fee invoice or statement',
                    'Student ID or enrollment confirmation'
                ];
            case 'MedicalExpenses':
                return [
                    'Medical appointment confirmation',
                    'Medical invoice or quote',
                    'Letter from medical provider (if available)'
                ];
            case 'Other':
                return [
                    'Detailed explanation of purpose',
                    'Any supporting documentation related to the purpose',
                    'Proof of transaction necessity'
                ];
            default:
                return ['Please select a purpose to see required documents'];
        }
    },

    areRequiredDocumentsUploaded: function(purpose, attachments) {
        return attachments.length > 0;
    },

    reset: function() {
        var defaultLimit = 0;
        var defaultLimitElement = document.getElementById('defaultVisaDebitLimit');
        if (defaultLimitElement && defaultLimitElement.value) {
            defaultLimit = parseFloat(defaultLimitElement.value) || 0;
        }

        this.applicantDetails = {
            fullName: '',
            tin: '',
            phone: '',
            email: ''
        };

        this.applicationDetails = {
            bank: '',
            purpose: '',
            currentLimit: defaultLimit,
            requestedNewLimit: '',
            date: '',
            destinationCountryId: '',
            destinationCountryIds: [],
            additionalComments: ''
        };
    }
};

document.addEventListener('DOMContentLoaded', function() {
    applicationData.initializeDefaults();
});
var attachmentData = {
    attachments: [],
    MAX_ATTACHMENTS: 10,

    addAttachment: function(attachment) {
        if (!attachment || !attachment.file) {
            return false;
        }

        if (this.attachments.length >= this.MAX_ATTACHMENTS) {
            return false;
        }

        if (typeof validationUtils !== 'undefined') {
            if (!validationUtils.isValidFilename(attachment.file.name)) {
                return false;
            }
            if (!validationUtils.isValidFileSize(attachment.file, 10)) {
                return false;
            }
        }

        this.attachments.push({
            file: attachment.file,
            documentType: attachment.documentType || 'document',
            attachmentId: attachment.attachmentId || null,
            dateUploaded: new Date()
        });

        return true;
    },

    removeAttachment: function(index) {
        if (typeof index !== 'number' || index < 0 || index >= this.attachments.length) {
            return false;
        }
        this.attachments.splice(index, 1);
        return true;
    },

    getAttachments: function() {
        return this.attachments.slice();
    },

    getAttachmentCount: function() {
        return this.attachments.length;
    },

    hasAttachments: function() {
        return this.attachments.length > 0;
    },

    getAttachmentById: function(attachmentId) {
        if (!attachmentId) {
            return null;
        }
        for (var i = 0; i < this.attachments.length; i++) {
            if (this.attachments[i].attachmentId === attachmentId) {
                return this.attachments[i];
            }
        }
        return null;
    },

    reset: function() {
        this.attachments = [];
    }
};
"use strict";

var ApplicantDetailsStep = function() {
    var validator;

    var init = function() {
        try {
            initValidation();
            bindEvents();
            initInputMasks();
            loadSavedData();
        } catch (error) {
            handleError('Initialization failed', error);
        }
    };

    var initValidation = function() {
        try {
            var form = document.querySelector('[data-wiz-panel="1"]');
            if (!form) {
                return;
            }

            validator = FormValidation.formValidation(form, {
                fields: {
                    fullName: {
                        validators: {
                            notEmpty: {
                                message: 'Full Name is required'
                            },
                            stringLength: {
                                min: 3,
                                max: 100,
                                message: 'Full Name must be between 3 and 100 characters'
                            },
                            regexp: {
                                regexp: /^[\p{L}\s\-'.]+$/u,
                                message: 'Full Name contains invalid characters'
                            }
                        }
                    },
                    tin: {
                        validators: {
                            notEmpty: {
                                message: 'TIN is required'
                            },
                            callback: {
                                message: 'TIN must be between 1 and 20 digits',
                                callback: function(input) {
                                    var tinValue = input.value.replace(/[^0-9]/g, '');
                                    return tinValue.length >= 1 && tinValue.length <= 20;
                                }
                            },
                            regexp: {
                                regexp: /^[0-9]{1,20}$/,
                                message: 'TIN must contain only numeric digits'
                            }
                        }
                    },
                    phone: {
                        validators: {
                            notEmpty: {
                                message: 'Phone number is required'
                            },
                            regexp: {
                                regexp: /^[0-9\-\+\(\)\s]{7,15}$/,
                                message: 'Please enter a valid phone number'
                            }
                        }
                    },
                    email: {
                        validators: {
                            notEmpty: {
                                message: 'Email is required'
                            },
                            emailAddress: {
                                message: 'Please enter a valid email address'
                            },
                            stringLength: {
                                max: 254,
                                message: 'Email address is too long'
                            }
                        }
                    }
                },
                plugins: {
                    trigger: new FormValidation.plugins.Trigger(),
                    bootstrap: new FormValidation.plugins.Bootstrap5({
                        rowSelector: '.wiz-field',
                        eleInvalidClass: '',
                        eleValidClass: ''
                    })
                }
            });
        } catch (error) {
            handleError('Validation initialization failed', error);
        }
    };

    var bindEvents = function() {
        try {
            var nextButton = document.getElementById('applicantDetailsNextBtn');
            if (nextButton) {
                nextButton.addEventListener('click', function(e) {
                    try {
                        e.preventDefault();
                        nextButton.setAttribute('data-kt-indicator', 'on');
                        nextButton.disabled = true;

                        validator.validate().then(function(status) {
                            if (status === 'Valid') {
                                saveData();
                                nextButton.removeAttribute('data-kt-indicator');
                                nextButton.disabled = false;
                                ApplicationForm.goToStep(2);
                            } else {
                                Swal.fire({
                                    text: "Please correct the errors in the form before proceeding.",
                                    icon: "error",
                                    buttonsStyling: false,
                                    confirmButtonText: "Ok",
                                    customClass: {
                                        confirmButton: "btn btn-primary"
                                    }
                                });
                                nextButton.removeAttribute('data-kt-indicator');
                                nextButton.disabled = false;
                            }
                        }).catch(function(error) {
                            handleError('Validation error', error);
                            nextButton.removeAttribute('data-kt-indicator');
                            nextButton.disabled = false;
                            showValidationError();
                        });
                    } catch (error) {
                        handleError('Click handler error', error);
                        nextButton.removeAttribute('data-kt-indicator');
                        nextButton.disabled = false;
                        showValidationError();
                    }
                });
            }
        } catch (error) {
            handleError('Event binding failed', error);
        }
    };

    var initInputMasks = function() {
        try {
            var tinInput = document.getElementById('tin');
            if (tinInput) {
                tinInput.addEventListener('input', function(e) {
                    var value = e.target.value.replace(/[^0-9]/g, '');
                    e.target.value = value.substring(0, 20);
                });
                if (!tinInput.hasAttribute('data-format')) {
                    tinInput.setAttribute('data-format', 'tin');
                }
            }

            var phoneInput = document.getElementById('phone');
            if (phoneInput) {
                phoneInput.addEventListener('input', function(e) {
                    var value = e.target.value.replace(/[^\d\+\-\s\(\)]/g, '');
                    e.target.value = value.substring(0, 20);
                });
                if (phoneInput.getAttribute('type') !== 'tel') {
                    phoneInput.setAttribute('type', 'tel');
                }
            }
        } catch (error) {
            handleError('Input mask initialization failed', error);
        }
    };

    var loadSavedData = function() {
        try {
            if (applicationData && applicationData.applicantDetails) {
                var details = applicationData.applicantDetails;
                setFieldValue('fullName', details.fullName);
                setFieldValue('tin', details.tin);
                setFieldValue('phone', details.phone);
                setFieldValue('email', details.email);
            }
        } catch (error) {
            handleError('Failed to load saved data', error);
        }
    };

    var saveData = function() {
        try {
            var tinInput = document.getElementById('tin');
            var rawTin = tinInput ? validationUtils.sanitizeTIN(tinInput.value) : '';

            var fullNameInput = document.getElementById('fullName');
            var phoneInput = document.getElementById('phone');
            var emailInput = document.getElementById('email');

            applicationData.applicantDetails = {
                fullName: fullNameInput ? validationUtils.sanitizeName(fullNameInput.value) : '',
                tin: rawTin,
                phone: phoneInput ? validationUtils.sanitizePhone(phoneInput.value) : '',
                email: emailInput ? validationUtils.sanitizeEmail(emailInput.value) : ''
            };
        } catch (error) {
            handleError('Failed to save data', error);
        }
    };

    var setFieldValue = function(fieldId, value) {
        var field = document.getElementById(fieldId);
        if (field && value) {
            field.value = value;
        }
    };

    var showValidationError = function() {
        Swal.fire({
            text: "An error occurred during validation. Please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok",
            customClass: {
                confirmButton: "btn btn-primary"
            }
        });
    };

    var handleError = function(context, error) {
        if (typeof console !== 'undefined' && console.error) {
            console.error(context + ':', error.message || error);
        }
    };

    return {
        init: function() {
            init();
        }
    };
}();

var applicantDetailsStep = ApplicantDetailsStep;
"use strict";

var ApplicationDetailsStep = function() {
    var validator;
    var isFormValid = false;

    var init = function() {
        try {
            applicationData.loadBankOptions(function(success) {
                applicationData.loadPurposeOptions(function(success) {
                    applicationData.loadDocumentTypesByPurpose(function(success) {
                        initializeStep();
                    });
                });
            });
        } catch (error) {
            handleError('Initialization failed', error);
        }
    };

    var initializeStep = function() {
        try {
            populateDropdowns();
            initSelect2();
            initValidation();
            loadCountries();
            bindEvents();
            initInputMasks();
            loadSavedData();
            if (applicationData.applicationDetails.purpose && typeof documentsStep !== 'undefined') {
                documentsStep.refreshRequirements();
            }
        } catch (error) {
            handleError('Step initialization failed', error);
        }
    };

    var initValidation = function() {
        try {
            var form = document.querySelector('#requestForm');
            if (!form) {
                return;
            }

            if (validator) {
                validator.destroy();
                validator = null;
            }

            validator = FormValidation.formValidation(form, {
                fields: {
                    bank: {
                        validators: {
                            notEmpty: {
                                message: 'Bank selection is required'
                            },
                            callback: {
                                message: 'Please select a valid bank',
                                callback: function(input) {
                                    var value = input.value;
                                    return value !== null && value !== undefined && value.trim() !== '';
                                }
                            }
                        }
                    },
                    purpose: {
                        validators: {
                            notEmpty: {
                                message: 'Purpose selection is required'
                            },
                            callback: {
                                message: 'Please select a valid purpose',
                                callback: function(input) {
                                    var value = input.value;
                                    return value !== null && value !== undefined && value.trim() !== '';
                                }
                            }
                        }
                    },
                    requestedNewLimit: {
                        validators: {
                            notEmpty: {
                                message: 'Requested New Limit is required'
                            },
                            callback: {
                                message: 'Please enter a valid amount',
                                callback: function(input) {
                                    var value = input.value;
                                    if (!value || value.trim() === '') {
                                        return false;
                                    }
                                    if (typeof parseCurrency === 'function') {
                                        value = parseCurrency(value);
                                    } else {
                                        value = value.replace(/[^0-9.]/g, '');
                                        value = parseFloat(value);
                                    }
                                    var currentLimit = parseFloat(document.getElementById('defaultVisaDebitLimit').value) || 5000;
                                    var currentLimitElem = document.getElementById('currentLimit');
                                    if (currentLimitElem && currentLimitElem.value) {
                                        currentLimit = parseFloat(currentLimitElem.value.replace(/[^0-9.]/g, ''));
                                    }
                                    if (isNaN(value) || value <= currentLimit) {
                                        this.message = 'Requested New Limit must be greater than the Delegated Monthly Limit';
                                        return false;
                                    }
                                    if (value > validationUtils.constants.MAX_REQUESTED_LIMIT) {
                                        this.message = 'Requested New Limit exceeds maximum allowed';
                                        return false;
                                    }
                                    return true;
                                }
                            }
                        }
                    },
                    date: {
                        validators: {
                            notEmpty: {
                                message: 'Usage Start Date is required'
                            },
                            callback: {
                                message: 'Date cannot be in the past',
                                callback: function(input) {
                                    if (!input.value) {
                                        return false;
                                    }
                                    var selectedDate = new Date(input.value);
                                    var today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    if (isNaN(selectedDate.getTime())) {
                                        this.message = 'Please enter a valid date';
                                        return false;
                                    }
                                    return selectedDate >= today;
                                }
                            }
                        }
                    },
                    destinationCountry: {
                        validators: {
                            notEmpty: {
                                message: 'Destination Country is required'
                            },
                            callback: {
                                message: 'Please select at least one destination country',
                                callback: function(input) {
                                    var values = $(input.element).val();
                                    if (Array.isArray(values)) {
                                        return values.length > 0;
                                    }
                                    return values !== null && values !== undefined && values.toString().trim() !== '';
                                }
                            }
                        }
                    }
                },
                plugins: {
                    trigger: new FormValidation.plugins.Trigger(),
                    bootstrap: new FormValidation.plugins.Bootstrap5({
                        rowSelector: '.wiz-field',
                        eleInvalidClass: '',
                        eleValidClass: ''
                    })
                }
            });

            validator.on('core.form.valid', function() {
                isFormValid = true;
            });

            validator.on('core.form.invalid', function() {
                isFormValid = false;
            });
        } catch (error) {
            handleError('Validation initialization failed', error);
        }
    };

    var initSelect2 = function() {
        try {
            $('#bank').select2({
                placeholder: 'Select Bank',
                allowClear: true,
                minimumResultsForSearch: 10
            }).on('change', function() {
                if (validator) {
                    try {
                        validator.revalidateField('bank');
                    } catch (e) {}
                }
            });

            $('#purpose').select2({
                placeholder: 'Select Purpose',
                allowClear: true,
                minimumResultsForSearch: 10,
                templateResult: formatPurposeOption,
                templateSelection: formatPurposeSelection
            }).on('change', function() {
                var selectedPurpose = $(this).val();
                var previousPurpose = applicationData.applicationDetails.purpose;
                if (validator) {
                    try {
                        validator.revalidateField('purpose');
                    } catch (e) {}
                }
                if (selectedPurpose !== previousPurpose) {
                    handlePurposeChange(selectedPurpose, previousPurpose);
                }
            });

            var countryTemplates = window.CountryFlagSelect || {};

            $('#destinationCountry').select2({
                placeholder: 'Select Destination Country/Countries',
                allowClear: true,
                multiple: true,
                minimumResultsForSearch: 10,
                // Falls back to plain text if the shared module has not loaded, rather than
                // breaking the dropdown over a decoration.
                templateResult: countryTemplates.templateResult,
                templateSelection: countryTemplates.templateSelection
            }).on('change', function() {
                if (validator) {
                    try {
                        validator.revalidateField('destinationCountry');
                    } catch (e) {}
                }
            });
        } catch (error) {
            handleError('Select2 initialization failed', error);
        }
    };

    var handlePurposeChange = function(selectedPurpose, previousPurpose) {
        try {
            applicationData.applicationDetails.purpose = selectedPurpose;
            var attachments = attachmentData.getAttachments();

            if (window.applicationDropzone && attachments.length > 0) {
                Swal.fire({
                    title: 'Change Purpose?',
                    text: 'Changing the purpose will clear your uploaded documents. Do you want to continue?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, change purpose',
                    cancelButtonText: 'No, keep current purpose',
                    buttonsStyling: false,
                    customClass: {
                        confirmButton: 'btn btn-danger',
                        cancelButton: 'btn btn-secondary'
                    }
                }).then(function(result) {
                    if (result.isConfirmed) {
                        window.applicationDropzone.removeAllFiles(true);
                        attachmentData.reset();
                        if (typeof documentsStep !== 'undefined') {
                            documentsStep.refreshRequirements();
                        }
                    } else {
                        applicationData.applicationDetails.purpose = previousPurpose;
                        $('#purpose').val(previousPurpose).trigger('change.select2');
                    }
                });
            } else if (typeof documentsStep !== 'undefined') {
                documentsStep.refreshRequirements();
            }
        } catch (error) {
            handleError('Purpose change failed', error);
        }
    };

    var formatPurposeOption = function(purpose) {
        try {
            if (!purpose.id) {
                return purpose.text;
            }
            var purposeText = validationUtils.encodeHtmlEntities(purpose.text || 'Unknown Purpose');
            var documentTypes = [];
            var documentTypeCount = 0;

            try {
                documentTypes = applicationData.getDocumentTypes(purpose.id) || [];
                documentTypeCount = documentTypes.length;
            } catch (err) {
                documentTypeCount = 0;
            }

            var container = document.createElement('div');
            container.className = 'd-flex flex-column';

            var nameSpan = document.createElement('span');
            nameSpan.className = 'fw-bold';
            nameSpan.textContent = purpose.text || 'Unknown Purpose';
            container.appendChild(nameSpan);

            if (documentTypeCount > 0) {
                var countSpan = document.createElement('span');
                countSpan.className = 'text-muted fs-7';
                countSpan.textContent = documentTypeCount + ' document type(s) required';
                container.appendChild(countSpan);
            }

            return $(container);
        } catch (error) {
            handleError('Purpose option format failed', error);
            return purpose.text || 'Unknown';
        }
    };

    var formatPurposeSelection = function(purpose) {
        return purpose.text;
    };

    var loadCountries = function() {
        try {
            var countrySelect = document.getElementById('destinationCountry');
            if (!countrySelect) {
                return;
            }

            while (countrySelect.options.length > 1) {
                countrySelect.remove(1);
            }

            var loadingOption = new Option("Loading countries...", "");
            loadingOption.disabled = true;
            countrySelect.add(loadingOption);

            axios.get(getApiUrl('/api/Country'))
                .then(function(response) {
                    countrySelect.remove(countrySelect.options.length - 1);
                    if (response.data && Array.isArray(response.data)) {
                        response.data.forEach(function(country) {
                            var option = new Option(country.name, country.id);
                            if (country.flag) {
                                // Read back by the shared flag formatter when select2 renders.
                                option.setAttribute('data-flag', country.flag);
                            }
                            countrySelect.add(option);
                        });

                        if (applicationData && applicationData.applicationDetails) {
                            var destinationCountryIds = applicationData.applicationDetails.destinationCountryIds;
                            var destinationCountryId = applicationData.applicationDetails.destinationCountryId;

                            if (destinationCountryIds && destinationCountryIds.length > 0) {
                                $(countrySelect).val(destinationCountryIds).trigger('change');
                            } else if (destinationCountryId) {
                                $(countrySelect).val([destinationCountryId]).trigger('change');
                            }
                        }
                    } else {
                        showMessage('Country data could not be loaded properly', 'warning');
                    }
                })
                .catch(function(error) {
                    if (countrySelect.options.length > 1) {
                        countrySelect.remove(countrySelect.options.length - 1);
                    }
                    showMessage('Could not load countries. Please try again later.', 'warning');
                });
        } catch (error) {
            handleError('Country loading failed', error);
        }
    };

    var populateDropdowns = function() {
        try {
            var bankSelect = document.getElementById('bank');
            if (bankSelect) {
                while (bankSelect.options.length > 1) {
                    bankSelect.remove(1);
                }
                applicationData.bankOptions.forEach(function(bank) {
                    var option = new Option(bank.text, bank.value);
                    bankSelect.add(option);
                });
            }

            var purposeSelect = document.getElementById('purpose');
            if (purposeSelect) {
                while (purposeSelect.options.length > 1) {
                    purposeSelect.remove(1);
                }
                if (applicationData.purposeOptions && applicationData.purposeOptions.length > 0) {
                    applicationData.purposeOptions.forEach(function(purpose) {
                        var option = new Option(purpose.text, purpose.value);
                        purposeSelect.add(option);
                    });
                } else {
                    var option = new Option("No options available", "");
                    option.disabled = true;
                    purposeSelect.add(option);
                }
            }
        } catch (error) {
            handleError('Dropdown population failed', error);
        }
    };

    var validateAllRequiredFields = function() {
        return new Promise(function(resolve) {
            try {
                if (!validator) {
                    resolve(false);
                    return;
                }

                validator.validate().then(function(status) {
                    var validationErrors = [];

                    var bank = document.getElementById('bank');
                    if (!bank || !bank.value || bank.value.trim() === '') {
                        validationErrors.push({ fieldId: 'bank', message: 'Bank is required' });
                    }

                    var purpose = document.getElementById('purpose');
                    if (!purpose || !purpose.value || purpose.value.trim() === '') {
                        validationErrors.push({ fieldId: 'purpose', message: 'Purpose is required' });
                    }

                    var requestedNewLimit = document.getElementById('requestedNewLimit');
                    if (!requestedNewLimit || !requestedNewLimit.value || requestedNewLimit.value.trim() === '') {
                        validationErrors.push({ fieldId: 'requestedNewLimit', message: 'Requested New Limit is required' });
                    } else {
                        var limitValue = requestedNewLimit.value.replace(/[^0-9.-]/g, '');
                        var numericValue = parseFloat(limitValue);
                        var currentLimit = parseFloat(document.getElementById('defaultVisaDebitLimit').value) || 5000;

                        var currentLimitInput = document.getElementById('currentLimit');
                        if (currentLimitInput && currentLimitInput.value) {
                            var cleanCurrentLimit = currentLimitInput.value.replace(/[^0-9.-]/g, '');
                            currentLimit = parseFloat(cleanCurrentLimit);
                        }

                        if (isNaN(numericValue) || numericValue <= currentLimit) {
                            validationErrors.push({ fieldId: 'requestedNewLimit', message: 'Requested New Limit must be greater than the Delegated Monthly Limit' });
                        }

                        if (numericValue > validationUtils.constants.MAX_REQUESTED_LIMIT) {
                            validationErrors.push({ fieldId: 'requestedNewLimit', message: 'Requested New Limit exceeds maximum allowed' });
                        }
                    }

                    var date = document.getElementById('date');
                    if (!date || !date.value || date.value.trim() === '') {
                        validationErrors.push({ fieldId: 'date', message: 'Usage Start Date is required' });
                    } else {
                        var selectedDate = new Date(date.value);
                        var today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (selectedDate < today) {
                            validationErrors.push({ fieldId: 'date', message: 'Usage Start Date cannot be in the past' });
                        }
                    }

                    var destinationCountrySelect = document.getElementById('destinationCountry');
                    var destinationCountryValues = $(destinationCountrySelect).val();
                    var hasDestinationCountry = Array.isArray(destinationCountryValues)
                        ? destinationCountryValues.length > 0
                        : (destinationCountryValues && destinationCountryValues.toString().trim() !== '');

                    if (!hasDestinationCountry) {
                        validationErrors.push({ fieldId: 'destinationCountry', message: 'At least one Destination Country is required' });
                    }

                    renderValidationSummary(validationErrors);

                    if (validationErrors.length > 0) {
                        showMessage('Please correct the errors below before proceeding.', 'error');
                        resolve(false);
                        return;
                    }

                    resolve(true);
                });
            } catch (error) {
                handleError('Validation failed', error);
                resolve(false);
            }
        });
    };

    var renderValidationSummary = function (errors) {
        var list = document.getElementById('validationErrors');
        var summary = document.getElementById('validationSummary');
        if (!list || !summary) { return; }

        list.innerHTML = '';

        errors.forEach(function (err) {
            var li = document.createElement('li');
            var link = document.createElement('a');
            link.href = '#' + err.fieldId;
            link.className = 'text-danger';
            link.textContent = err.message;
            link.addEventListener('click', function (e) {
                e.preventDefault();
                var target = document.getElementById(err.fieldId);
                if (target) { target.focus(); }
            });
            li.appendChild(link);
            list.appendChild(li);
        });

        if (errors.length) {
            summary.classList.remove('d-none');
            summary.setAttribute('role', 'alert');
        } else {
            summary.classList.add('d-none');
        }
    };

    var bindEvents = function() {
        try {
            var prevButton = document.getElementById('applicationDetailsPrevBtn');
            if (prevButton) {
                prevButton.addEventListener('click', function(e) {
                    e.preventDefault();
                    saveData();
                    ApplicationForm.goToStep(1);
                });
            }

            var nextButton = document.getElementById('applicationDetailsNextBtn');
            if (nextButton) {
                nextButton.addEventListener('click', function(e) {
                    e.preventDefault();
                    nextButton.setAttribute('data-kt-indicator', 'on');
                    nextButton.disabled = true;

                    validateAllRequiredFields().then(function(isValid) {
                        if (!isValid) {
                            showMessage('Please fill in all required fields correctly before proceeding.', 'error');
                            focusFirstInvalidField();
                            nextButton.removeAttribute('data-kt-indicator');
                            nextButton.disabled = false;
                            return;
                        }

                        saveData();
                        nextButton.removeAttribute('data-kt-indicator');
                        nextButton.disabled = false;
                        ApplicationForm.goToStep(3);
                    }).catch(function(error) {
                        handleError('Validation error', error);
                        nextButton.removeAttribute('data-kt-indicator');
                        nextButton.disabled = false;
                        showMessage('An error occurred during validation. Please try again.', 'error');
                    });
                });
            }

            bindFieldValidationEvents();
        } catch (error) {
            handleError('Event binding failed', error);
        }
    };

    var bindFieldValidationEvents = function() {
        try {
            var requiredFields = ['requestedNewLimit', 'date'];
            requiredFields.forEach(function(fieldId) {
                var field = document.getElementById(fieldId);
                if (field) {
                    field.addEventListener('blur', function() {
                        if (validator) {
                            validator.revalidateField(fieldId);
                        }
                    });
                }
            });
        } catch (error) {
            handleError('Field validation binding failed', error);
        }
    };

    var initInputMasks = function() {
        try {
            var dateInput = document.getElementById('date');
            if (dateInput && typeof flatpickr !== 'undefined') {
                flatpickr(dateInput, {
                    dateFormat: 'Y-m-d',
                    minDate: 'today',
                    allowInput: false
                });
            }

            var limitInput = document.getElementById('requestedNewLimit');
            if (limitInput) {
                limitInput.addEventListener('input', function(e) {
                    var value = e.target.value.replace(/[^0-9.]/g, '');
                    var parts = value.split('.');
                    if (parts.length > 2) {
                        value = parts[0] + '.' + parts.slice(1).join('');
                    }
                    e.target.value = value;
                });
            }
        } catch (error) {
            handleError('Input mask initialization failed', error);
        }
    };

    var loadSavedData = function() {
        try {
            if (applicationData && applicationData.applicationDetails) {
                var details = applicationData.applicationDetails;

                if (details.bank) {
                    $('#bank').val(details.bank).trigger('change');
                }
                if (details.purpose) {
                    $('#purpose').val(details.purpose).trigger('change');
                }
                if (details.requestedNewLimit) {
                    var limitInput = document.getElementById('requestedNewLimit');
                    if (limitInput) {
                        limitInput.value = details.requestedNewLimit;
                    }
                }
                if (details.date) {
                    var dateInput = document.getElementById('date');
                    if (dateInput) {
                        dateInput.value = details.date;
                    }
                }
                if (details.additionalComments) {
                    var commentsInput = document.getElementById('additionalComments');
                    if (commentsInput) {
                        commentsInput.value = details.additionalComments;
                    }
                }
            }
        } catch (error) {
            handleError('Data loading failed', error);
        }
    };

    var saveData = function() {
        try {
            var bankSelect = document.getElementById('bank');
            var purposeSelect = document.getElementById('purpose');
            var limitInput = document.getElementById('requestedNewLimit');
            var dateInput = document.getElementById('date');
            var commentsInput = document.getElementById('additionalComments');
            var destinationSelect = document.getElementById('destinationCountry');

            var destinationCountryValues = $(destinationSelect).val();

            applicationData.applicationDetails = {
                bank: bankSelect ? bankSelect.value : '',
                purpose: purposeSelect ? purposeSelect.value : '',
                currentLimit: parseFloat(document.getElementById('defaultVisaDebitLimit').value) || 5000,
                requestedNewLimit: limitInput ? limitInput.value.replace(/[^0-9.]/g, '') : '',
                date: dateInput ? dateInput.value : '',
                destinationCountryIds: Array.isArray(destinationCountryValues) ? destinationCountryValues : [],
                destinationCountryId: Array.isArray(destinationCountryValues) && destinationCountryValues.length > 0 ? destinationCountryValues[0] : '',
                additionalComments: commentsInput ? commentsInput.value.substring(0, validationUtils.constants.MAX_COMMENTS_LENGTH) : ''
            };
        } catch (error) {
            handleError('Data saving failed', error);
        }
    };

    var showMessage = function(message, type) {
        try {
            var alertElement = document.getElementById('alertMessage');
            var alertMessageText = document.getElementById('alertMessageText');

            if (alertElement && alertMessageText) {
                alertMessageText.textContent = message;
                alertElement.classList.remove('alert-success', 'alert-danger', 'alert-warning', 'alert-info');
                alertElement.classList.add('alert-' + type);
                alertElement.classList.remove('d-none');
                alertElement.classList.add('d-flex');
                alertElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

                setTimeout(function() {
                    alertElement.classList.remove('d-flex');
                    alertElement.classList.add('d-none');
                }, 5000);
            }
        } catch (error) {
            handleError('Message display failed', error);
        }
    };

    var focusFirstInvalidField = function() {
        var invalidField = document.querySelector('.is-invalid');
        if (invalidField) {
            invalidField.focus();
            invalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    var scrollToElement = function(selector) {
        var element = document.querySelector(selector);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    var handleError = function(context, error) {
        if (typeof console !== 'undefined' && console.error) {
            console.error(context + ':', error.message || error);
        }
    };

    return {
        init: function() {
            init();
        }
    };
}();

var applicationDetailsStep = ApplicationDetailsStep;
"use strict";

var recaptchaWidgetId = null;
var recaptchaRendered = false;

var DeclarationStep = function() {
    var recaptchaSiteKey = null;
    var recaptchaResponse = null;
    var isSubmitting = false;
    var lastSubmitTime = 0;
    var SUBMIT_COOLDOWN_MS = 5000;

    var init = function() {
        try {
            var siteKeyField = document.getElementById('recaptchaSiteKey');
            if (siteKeyField && siteKeyField.value) {
                recaptchaSiteKey = siteKeyField.value;
                tryRenderRecaptcha();
            }
            bindEvents();
        } catch (error) {
            handleError('Initialization failed', error);
        }
    };

    var tryRenderRecaptcha = function() {
        if (recaptchaRendered) {
            return;
        }

        if (window.recaptchaReady && typeof grecaptcha !== 'undefined') {
            renderRecaptcha();
        } else {
            if (window.recaptchaQueue) {
                window.recaptchaQueue.push(renderRecaptcha);
            } else {
                setTimeout(tryRenderRecaptcha, 500);
            }
        }
    };

    var renderRecaptcha = function() {
        if (recaptchaRendered) {
            return;
        }

        var container = document.getElementById('recaptcha-container');
        if (!container) {
            return;
        }

        if (!recaptchaSiteKey) {
            return;
        }

        if (typeof grecaptcha === 'undefined' || typeof grecaptcha.render !== 'function') {
            setTimeout(tryRenderRecaptcha, 500);
            return;
        }

        try {
            recaptchaWidgetId = grecaptcha.render(container, {
                'sitekey': recaptchaSiteKey,
                'callback': function(token) {
                    recaptchaResponse = token;
                    var errorEl = document.getElementById('recaptchaError');
                    if (errorEl) errorEl.textContent = '';
                },
                'expired-callback': function() {
                    recaptchaResponse = null;
                    var errorEl = document.getElementById('recaptchaError');
                    if (errorEl) errorEl.textContent = 'Verification expired. Please verify again.';
                },
                'error-callback': function() {
                    recaptchaResponse = null;
                    var errorEl = document.getElementById('recaptchaError');
                    if (errorEl) errorEl.textContent = 'Verification error. Please refresh.';
                }
            });
            recaptchaRendered = true;
        } catch (error) {
            handleError('reCAPTCHA render failed', error);
        }
    };

    var bindEvents = function() {
        var declarationCheck = document.getElementById('declarationCheck');
        if (declarationCheck) {
            declarationCheck.addEventListener('change', function() {
                validateDeclaration();
            });
        }

        var submitButton = document.getElementById('submitApplicationBtn');
        if (submitButton) {
            submitButton.addEventListener('click', function(e) {
                e.preventDefault();

                if (isSubmitting) {
                    showMessage('Please wait, your submission is being processed.', 'warning');
                    return;
                }

                var now = Date.now();
                if (now - lastSubmitTime < SUBMIT_COOLDOWN_MS) {
                    var remainingSeconds = Math.ceil((SUBMIT_COOLDOWN_MS - (now - lastSubmitTime)) / 1000);
                    showMessage('Please wait ' + remainingSeconds + ' seconds before submitting again.', 'warning');
                    return;
                }

                if (validateStep()) {
                    submitButton.setAttribute('data-kt-indicator', 'on');
                    submitButton.disabled = true;
                    submitApplication();
                }
            });
        }
    };

    var validateDeclaration = function() {
        var declarationCheck = document.getElementById('declarationCheck');
        var errorElement = document.getElementById('declarationCheckError');

        if (!declarationCheck || !errorElement) return false;

        if (!declarationCheck.checked) {
            errorElement.textContent = 'You must accept the declaration to proceed';
            return false;
        }

        errorElement.textContent = '';
        return true;
    };

    var validateRecaptcha = function() {
        if (!recaptchaSiteKey) {
            return true;
        }

        var errorElement = document.getElementById('recaptchaError');
        var token = recaptchaResponse;

        if (!token && typeof grecaptcha !== 'undefined' && recaptchaWidgetId !== null) {
            try {
                token = grecaptcha.getResponse(recaptchaWidgetId);
            } catch (e) {
                handleError('reCAPTCHA response error', e);
            }
        }

        if (!token) {
            if (errorElement) {
                errorElement.textContent = 'Please complete the security verification.';
            }
            return false;
        }

        recaptchaResponse = token;
        if (errorElement) {
            errorElement.textContent = '';
        }
        return true;
    };

    var validateStep = function() {
        var isDeclarationValid = validateDeclaration();
        var isRecaptchaValid = validateRecaptcha();
        return isDeclarationValid && isRecaptchaValid;
    };

    var resetRecaptcha = function() {
        try {
            if (typeof grecaptcha !== 'undefined' && recaptchaWidgetId !== null) {
                grecaptcha.reset(recaptchaWidgetId);
                recaptchaResponse = null;
            }
        } catch (error) {
            handleError('reCAPTCHA reset failed', error);
        }
    };

    var resetSubmitButton = function() {
        isSubmitting = false;
        var submitButton = document.getElementById('submitApplicationBtn');
        if (submitButton) {
            submitButton.removeAttribute('data-kt-indicator');
            submitButton.disabled = false;
        }
        if (typeof RbfPageLoader !== 'undefined') {
            RbfPageLoader.hide();
        }
    };

    var submitApplication = function() {
        isSubmitting = true;
        lastSubmitTime = Date.now();

        var destinationCountryIds = applicationData.applicationDetails.destinationCountryIds;
        var destinationCountryIdsString = null;

        if (Array.isArray(destinationCountryIds) && destinationCountryIds.length > 0) {
            destinationCountryIdsString = destinationCountryIds.map(function(id) {
                return String(id);
            }).join(',');
        }

        var firstCountryId = null;
        if (Array.isArray(destinationCountryIds) && destinationCountryIds.length > 0) {
            firstCountryId = parseInt(destinationCountryIds[0], 10);
            if (isNaN(firstCountryId)) firstCountryId = null;
        } else if (applicationData.applicationDetails.destinationCountryId) {
            var idValue = applicationData.applicationDetails.destinationCountryId;
            firstCountryId = parseInt(String(idValue), 10);
            if (isNaN(firstCountryId)) firstCountryId = null;
        }

        var token = recaptchaResponse;
        if (!token && typeof grecaptcha !== 'undefined' && recaptchaWidgetId !== null) {
            try {
                token = grecaptcha.getResponse(recaptchaWidgetId);
            } catch (e) {
                handleError('reCAPTCHA token error', e);
            }
        }

        var requestedLimit = parseFloat(applicationData.applicationDetails.requestedNewLimit);
        if (isNaN(requestedLimit) || requestedLimit <= 0) {
            resetSubmitButton();
            resetRecaptcha();
            ApplicationForm.showErrorMessage('Invalid requested limit amount.');
            return;
        }

        if (requestedLimit > validationUtils.constants.MAX_REQUESTED_LIMIT) {
            resetSubmitButton();
            resetRecaptcha();
            ApplicationForm.showErrorMessage('Requested limit exceeds maximum allowed.');
            return;
        }

        var applicationModelData = {
            fullName: validationUtils.sanitizeName(applicationData.applicantDetails.fullName),
            taxIdNumber: validationUtils.sanitizeTIN(applicationData.applicantDetails.tin),
            phone: validationUtils.sanitizePhone(applicationData.applicantDetails.phone),
            email: validationUtils.sanitizeEmail(applicationData.applicantDetails.email),
            bank: applicationData.applicationDetails.bank,
            purpose: applicationData.applicationDetails.purpose,
            currentLimit: applicationData.applicationDetails.currentLimit,
            requestedNewLimit: requestedLimit,
            destinationCountryId: firstCountryId,
            destinationCountryIds: destinationCountryIdsString,
            applicationDate: applicationData.applicationDetails.date ?
                new Date(applicationData.applicationDetails.date).toISOString() : null,
            additionalComments: applicationData.applicationDetails.additionalComments ?
                applicationData.applicationDetails.additionalComments.substring(0, validationUtils.constants.MAX_COMMENTS_LENGTH) : '',
            declaration: true,
            attachments: {},
            recaptchaToken: token
        };

        var attachments = attachmentData.getAttachments();
        attachments.forEach(function(attachment, index) {
            var key = attachment.attachmentId || "attachment_" + index;
            applicationModelData.attachments[key] = {
                id: attachment.attachmentId || 0,
                requiredDocumentId: null,
                filename: validationUtils.sanitizeFilename(attachment.file.name)
            };
        });

        // Taken down again by resetSubmitButton(), which every outcome below goes through
        if (typeof RbfPageLoader !== 'undefined') {
            RbfPageLoader.show('Submitting your application');
        }

        axios.post(getApiUrl('/Application/Submit'), applicationModelData, {
            headers: getJsonRequestHeaders(),
            timeout: 30000
        }).then(function(response) {
            resetSubmitButton();

            if (response.data && response.data.success) {
                ApplicationForm.showSuccessMessage(
                    response.data.message || 'Your application has been submitted successfully.',
                    response.data.applicationId
                );
            } else {
                resetRecaptcha();
                ApplicationForm.showErrorMessage(
                    response.data && response.data.message ? response.data.message : 'An error occurred. Please try again.'
                );
            }
        }).catch(function(error) {
            resetSubmitButton();
            resetRecaptcha();

            var errorMessage = 'An error occurred while submitting your application.';
            if (error.response && error.response.status === 429) {
                errorMessage = 'Too many requests. Please wait a moment and try again.';
            } else if (typeof getMessageFromError === 'function') {
                errorMessage = getMessageFromError(error) || errorMessage;
            }

            ApplicationForm.showErrorMessage(errorMessage);
        });
    };

    var showMessage = function(message, type) {
        Swal.fire({
            text: message,
            icon: type === 'warning' ? 'warning' : 'info',
            buttonsStyling: false,
            confirmButtonText: 'Ok',
            customClass: {
                confirmButton: 'btn btn-primary'
            }
        });
    };

    var handleError = function(context, error) {
        if (typeof console !== 'undefined' && console.error) {
            console.error(context + ':', error.message || error);
        }
    };

    return {
        init: function() {
            init();
        },
        renderRecaptcha: function() {
            tryRenderRecaptcha();
        }
    };
}();

var declarationStep = DeclarationStep;
"use strict";

var DocumentsStep = function() {

    var initDropzone = function() {
        try {
            var dropzoneElement = document.querySelector('#document-dropzone');
            if (!dropzoneElement) {
                return;
            }

            var dropzone = new Dropzone(dropzoneElement, {
                url: getApiUrl('/ApplicationAttachment/UploadSupportingEvidence'),
                paramName: 'file',
                maxFiles: 5,
                maxFilesize: 10,
                addRemoveLinks: true,
                acceptedFiles: '.pdf,.jpg,.jpeg,.png,.doc,.docx',
                autoProcessQueue: true,
                uploadMultiple: false,
                headers: getRequestHeaders(),
                disabled: true,
                init: function() {
                    var dz = this;

                    this.on('addedfile', function(file) {
                        if (!applicationData.applicationDetails.purpose) {
                            showMessage('Please select a purpose before uploading documents', 'warning');
                            dz.removeFile(file);
                            return;
                        }

                        if (!validationUtils.isValidFilename(file.name)) {
                            showMessage('Invalid filename. Please rename your file and try again.', 'error');
                            dz.removeFile(file);
                            return;
                        }

                        if (!validationUtils.isValidFileSize(file, 10)) {
                            showMessage('File size exceeds the 10MB limit.', 'error');
                            dz.removeFile(file);
                            return;
                        }
                    });

                    this.on('success', function(file, response) {
                        if (response.success) {
                            file.attachmentId = response.attachmentId;
                            attachmentData.addAttachment({
                                file: file,
                                documentType: 'document',
                                attachmentId: response.attachmentId
                            });
                            renderUploaded();
                            renderRequirements();
                            hideDocumentError();
                        } else {
                            showMessage(response.message || 'Upload failed', 'error');
                            this.removeFile(file);
                        }
                    });

                    this.on('error', function(file, errorMessage) {
                        showMessage(typeof errorMessage === 'string' ? errorMessage : 'File upload failed', 'error');
                        this.removeFile(file);
                    });

                    var attachBtn = document.querySelector('.dropzone-select');
                    if (attachBtn) {
                        attachBtn.addEventListener('click', function(e) {
                            if (!applicationData.applicationDetails.purpose) {
                                e.preventDefault();
                                showMessage('Please select a purpose before uploading documents', 'warning');
                                return false;
                            }
                            dz.hiddenFileInput.click();
                        });
                    }

                    var removeAllBtn = document.querySelector('.dropzone-remove-all');
                    if (removeAllBtn) {
                        removeAllBtn.addEventListener('click', function() {
                            dz.removeAllFiles(true);
                            attachmentData.reset();
                            renderUploaded();
                            renderRequirements();
                        });
                    }
                }
            });

            window.applicationDropzone = dropzone;
        } catch (error) {
            handleError('Dropzone initialization failed', error);
        }
    };

    var renderRequirements = function() {
        try {
            var requirementsList = document.getElementById('documentRequirements');
            if (!requirementsList) {
                return;
            }

            while (requirementsList.firstChild) {
                requirementsList.removeChild(requirementsList.firstChild);
            }

            var purpose = document.getElementById('purpose');
            var selectedPurpose = purpose ? purpose.value : '';
            var attachmentCount = attachmentData.getAttachmentCount();

            if (selectedPurpose) {
                if (window.applicationDropzone) {
                    window.applicationDropzone.enable();
                }

                var requirements = applicationData.getDocumentRequirements(selectedPurpose);

                if (requirements && requirements.length > 0) {
                    // The upload flow does not tag files with a specific document type
                    // (attachmentData always stores documentType: 'document'), so we cannot
                    // honestly claim any single named requirement is satisfied. Only the
                    // aggregate count is genuinely known — see renderUploadProgress.
                    requirements.forEach(function(requirement) {
                        var li = document.createElement('li');
                        li.classList.add('wiz-checklist__item');
                        li.textContent = requirement;
                        requirementsList.appendChild(li);
                    });
                    renderUploadProgress(requirements.length, attachmentCount);
                } else {
                    var li = document.createElement('li');
                    li.classList.add('wiz-checklist__item');
                    li.textContent = 'Upload supporting documents for your application';
                    requirementsList.appendChild(li);
                    renderUploadProgress(null, attachmentCount);
                }
            } else {
                if (window.applicationDropzone) {
                    window.applicationDropzone.disable();
                }

                var emptyLi = document.createElement('li');
                emptyLi.classList.add('wiz-checklist__item');
                emptyLi.textContent = 'Please select a purpose to see required documents';
                requirementsList.appendChild(emptyLi);
                renderUploadProgress(null, attachmentCount);
            }
        } catch (error) {
            handleError('Document requirements update failed', error);
        }
    };

    var renderUploadProgress = function(requiredCount, attachmentCount) {
        try {
            var progress = document.getElementById('documentUploadProgress');
            if (!progress) {
                return;
            }

            if (typeof requiredCount === 'number' && requiredCount > 0) {
                var uploaded = Math.min(attachmentCount, requiredCount);
                progress.textContent = uploaded + ' of ' + requiredCount + ' required documents uploaded';
                progress.classList.remove('d-none');
            } else {
                progress.classList.add('d-none');
            }
        } catch (error) {
            handleError('Upload progress update failed', error);
        }
    };

    var renderUploaded = function() {
        try {
            var attachments = attachmentData.getAttachments();
            var container = document.getElementById('uploadedAttachments');
            if (!container) {
                return;
            }

            while (container.firstChild) {
                container.removeChild(container.firstChild);
            }

            if (attachments.length === 0) {
                var p = document.createElement('p');
                p.className = 'text-muted';
                p.textContent = 'No documents uploaded yet.';
                container.appendChild(p);
                return;
            }

            var listGroup = document.createElement('div');
            listGroup.className = 'rounded border p-5';

            var header = document.createElement('div');
            header.className = 'mb-5';
            var h4 = document.createElement('h4');
            h4.className = 'mb-0 fw-bold';
            h4.textContent = 'Uploaded Documents';
            header.appendChild(h4);
            listGroup.appendChild(header);

            attachments.forEach(function(attachment, index) {
                var sanitizedName = validationUtils.sanitizeFilename(attachment.file.name);

                var item = document.createElement('div');
                item.className = 'd-flex flex-stack py-3 border-bottom';
                item.setAttribute('data-attachment-name', sanitizedName);

                var fileInfo = document.createElement('div');
                fileInfo.className = 'd-flex align-items-center';

                var fileIcon = document.createElement('div');
                fileIcon.className = 'me-3';
                var icon = document.createElement('i');
                icon.className = getFileIcon(attachment.file.type) + ' fs-2x text-primary';
                fileIcon.appendChild(icon);
                fileInfo.appendChild(fileIcon);

                var fileDetails = document.createElement('div');
                fileDetails.className = 'ms-2';

                var fileName = document.createElement('span');
                fileName.className = 'fs-5 fw-bold text-gray-900 d-block';
                fileName.textContent = sanitizedName;
                fileDetails.appendChild(fileName);

                var fileSize = document.createElement('span');
                fileSize.className = 'fs-7 text-muted';
                fileSize.textContent = formatBytes(attachment.file.size);
                fileDetails.appendChild(fileSize);

                if (attachment.attachmentId) {
                    var uploadStatus = document.createElement('span');
                    uploadStatus.className = 'fs-7 text-success d-block';
                    uploadStatus.textContent = 'Uploaded successfully';
                    fileDetails.appendChild(uploadStatus);
                }

                fileInfo.appendChild(fileDetails);
                item.appendChild(fileInfo);

                var removeButton = document.createElement('button');
                removeButton.type = 'button';
                removeButton.className = 'btn btn-icon btn-sm btn-light-danger';
                var removeIcon = document.createElement('i');
                removeIcon.className = 'fas fa-times fs-2';
                removeButton.appendChild(removeIcon);
                removeButton.dataset.index = index;
                removeButton.addEventListener('click', function() {
                    attachmentData.removeAttachment(parseInt(this.dataset.index));
                    renderUploaded();
                    renderRequirements();
                });

                item.appendChild(removeButton);
                listGroup.appendChild(item);
            });

            container.appendChild(listGroup);
        } catch (error) {
            handleError('Attachment display failed', error);
        }
    };

    var validateDocuments = function() {
        try {
            var purpose = applicationData.applicationDetails.purpose;
            var attachments = attachmentData.getAttachments();

            if (!applicationData.areRequiredDocumentsUploaded(purpose, attachments)) {
                showDocumentError('Please upload all required documents before proceeding.');
                return false;
            }

            hideDocumentError();
            return true;
        } catch (error) {
            handleError('Document validation failed', error);
            return false;
        }
    };

    var getFileIcon = function(fileType) {
        try {
            if (fileType.startsWith('image/')) {
                return 'fas fa-file-image';
            } else if (fileType === 'application/pdf') {
                return 'fas fa-file-pdf';
            } else if (fileType.includes('word') || fileType.includes('document')) {
                return 'fas fa-file-word';
            } else if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
                return 'fas fa-file-excel';
            } else {
                return 'fas fa-file';
            }
        } catch (error) {
            return 'fas fa-file';
        }
    };

    var formatBytes = function(bytes, decimals) {
        try {
            decimals = decimals || 2;
            if (bytes === 0) return '0 Bytes';
            var k = 1024;
            var dm = decimals < 0 ? 0 : decimals;
            var sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
            var i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
        } catch (error) {
            return bytes + ' Bytes';
        }
    };

    // Must use the wizard-level alert (#errorAlert, rendered outside #wizPanels), not the
    // per-step #alertMessage: that one lives inside step 2's panel, which carries d-none
    // while the applicant is on this step, so anything written into it - including the
    // rejection notice for a file the scanner flagged as malicious - was never visible.
    var showMessage = function(message, type) {
        try {
            if (typeof ApplicationForm !== 'undefined' && ApplicationForm.showErrorMessage) {
                ApplicationForm.showErrorMessage(message);
                return;
            }

            // The applicant must not be left believing a rejected file was accepted, so if
            // the wizard shell is somehow unavailable, say it some other way rather than
            // swallowing it.
            window.alert(message);
        } catch (error) {
            handleError('Message display failed', error);
        }
    };

    var showDocumentError = function(message) {
        var errorContainer = document.getElementById('documentValidationError');
        var errorMessage = document.getElementById('documentErrorMessage');
        if (errorContainer && errorMessage) {
            errorMessage.textContent = message;
            errorContainer.classList.remove('d-none');
        }
    };

    var hideDocumentError = function() {
        var errorContainer = document.getElementById('documentValidationError');
        if (errorContainer) {
            errorContainer.classList.add('d-none');
        }
    };

    var handleError = function(context, error) {
        if (typeof console !== 'undefined' && console.error) {
            console.error(context + ':', error.message || error);
        }
    };

    var bindEvents = function () {
        var prevBtn = document.getElementById('documentsPrevBtn');
        var nextBtn = document.getElementById('documentsNextBtn');

        if (prevBtn) {
            prevBtn.addEventListener('click', function () {
                ApplicationForm.goToStep(2);
            });
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', function () {
                if (validateDocuments()) {
                    ApplicationForm.goToStep(4);
                }
            });
        }
    };

    return {
        init: function () {
            initDropzone();
            renderUploaded();
            bindEvents();
        },
        refreshRequirements: function () {
            renderRequirements();
            renderUploaded();
        }
    };
}();

var documentsStep = DocumentsStep;

"use strict";

var ReviewStep = function () {

    var row = function (label, value) {
        if (value === undefined || value === null || value === '') {
            value = '—';
        }
        return '<div class="wiz-summary__row"><dt>' +
               validationUtils.encodeHtmlEntities(label) + '</dt><dd>' +
               validationUtils.encodeHtmlEntities(String(value)) + '</dd></div>';
    };

    var textOf = function (elementId) {
        var el = document.getElementById(elementId);
        if (!el) { return ''; }
        if (el.tagName === 'SELECT') {
            var picked = [];
            Array.prototype.forEach.call(el.selectedOptions, function (opt) { picked.push(opt.text); });
            return picked.join(', ');
        }
        return el.value;
    };

    var render = function () {
        try {
            var applicant = document.getElementById('reviewApplicant');
            if (applicant) {
                applicant.innerHTML =
                    row('Full Name', textOf('fullName')) +
                    row('TIN', textOf('tin')) +
                    row('Phone', textOf('phone')) +
                    row('Email', textOf('email'));
            }

            var request = document.getElementById('reviewRequest');
            if (request) {
                request.innerHTML =
                    row('Bank', textOf('bank')) +
                    row('Purpose', textOf('purpose')) +
                    row('Delegated Monthly Limit', textOf('currentLimit')) +
                    row('Requested New Limit', textOf('requestedNewLimit')) +
                    row('Usage Start Date', textOf('date')) +
                    row('Destination Countries', textOf('destinationCountry')) +
                    row('Additional Comments', textOf('additionalComments'));
            }

            var docs = document.getElementById('reviewDocuments');
            if (docs) {
                var names = [];
                var items = document.querySelectorAll('#uploadedAttachments [data-attachment-name]');
                Array.prototype.forEach.call(items, function (el) {
                    names.push(el.getAttribute('data-attachment-name'));
                });
                docs.innerHTML = names.length
                    ? names.map(function (n, i) { return row('Document ' + (i + 1), n); }).join('')
                    : row('Documents', 'None uploaded');
            }
        } catch (error) {
            if (console && console.error) { console.error('Review render failed:', error); }
        }
    };

    var bindEvents = function () {
        var edits = document.querySelectorAll('[data-wiz-edit]');
        Array.prototype.forEach.call(edits, function (btn) {
            btn.addEventListener('click', function () {
                ApplicationForm.goToStep(parseInt(btn.getAttribute('data-wiz-edit'), 10));
            });
        });

        var prevBtn = document.getElementById('reviewPrevBtn');
        if (prevBtn) {
            prevBtn.addEventListener('click', function () { ApplicationForm.goToStep(3); });
        }
    };

    return {
        init: function () { bindEvents(); },
        render: function () { render(); }
    };
}();

var reviewStep = ReviewStep;

"use strict";

var ApplicationForm = function () {
    var TOTAL_STEPS = 4;
    var currentStepIndex = 1;

    var init = function () {
        try {
            updateStepIndicators(1);
            applicantDetailsStep.init();
            applicationDetailsStep.init();
            if (typeof documentsStep !== 'undefined') { documentsStep.init(); }
            if (typeof reviewStep !== 'undefined') { reviewStep.init(); }
            declarationStep.init();
        } catch (error) {
            handleError('Application form initialization failed', error);
        }
    };

    var updateStepIndicators = function (currentStep) {
        try {
            var items = document.querySelectorAll('[data-wiz-step]');
            Array.prototype.forEach.call(items, function (el) {
                var idx = parseInt(el.getAttribute('data-wiz-step'), 10);
                el.classList.remove('is-complete', 'is-current', 'is-pending');
                el.removeAttribute('aria-current');

                if (idx < currentStep) {
                    el.classList.add('is-complete');
                } else if (idx === currentStep) {
                    el.classList.add('is-current');
                    el.setAttribute('aria-current', 'step');
                } else {
                    el.classList.add('is-pending');
                }
            });

            var counter = document.getElementById('wizStepCounter');
            if (counter) {
                counter.textContent = 'Step ' + currentStep + ' of ' + TOTAL_STEPS;
            }
        } catch (error) {
            handleError('Step indicator update failed', error);
        }
    };

    var goToStep = function (stepIndex) {
        try {
            if (stepIndex < 1 || stepIndex > TOTAL_STEPS) {
                return;
            }

            var panels = document.querySelectorAll('[data-wiz-panel]');
            Array.prototype.forEach.call(panels, function (panel) {
                var idx = parseInt(panel.getAttribute('data-wiz-panel'), 10);
                if (idx === stepIndex) {
                    panel.classList.remove('d-none');
                } else {
                    panel.classList.add('d-none');
                }
            });

            hideErrorMessage();

            currentStepIndex = stepIndex;
            updateStepIndicators(stepIndex);

            if (stepIndex === TOTAL_STEPS && typeof reviewStep !== 'undefined') {
                reviewStep.render();
            }

            var heading = document.querySelector('[data-wiz-panel]:not(.d-none) .wiz-panel__title');
            if (heading) {
                heading.setAttribute('tabindex', '-1');
                heading.focus();
            }

            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            handleError('Navigation failed', error);
        }
    };

    // An error raised on one step is about that step, so it must not follow the applicant
    // to the next one.
    var hideErrorMessage = function () {
        var errorAlert = document.getElementById('errorAlert');

        if (errorAlert) {
            errorAlert.classList.remove('d-flex');
            errorAlert.classList.add('d-none');
        }
    };

    var showErrorMessage = function (message) {
        var errorAlert = document.getElementById('errorAlert');
        var errorMessage = document.getElementById('errorAlertMessage');

        if (errorAlert && errorMessage) {
            errorMessage.textContent = message;
            errorAlert.classList.remove('d-none');
            errorAlert.classList.add('d-flex');
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    var showSuccessMessage = function (message, applicationId) {
        var panels = document.querySelectorAll('[data-wiz-panel]');
        Array.prototype.forEach.call(panels, function (panel) {
            panel.classList.add('d-none');
        });
        var progress = document.getElementById('wizProgress');
        if (progress) { progress.classList.add('d-none'); }

        var successStep = document.getElementById('successStep');
        if (successStep) {
            successStep.classList.remove('d-none');
            var successMessage = document.getElementById('successMessage');
            var applicationIdElement = document.getElementById('applicationId');

            if (successMessage) {
                successMessage.textContent = message;
            }

            if (applicationIdElement && applicationId) {
                applicationIdElement.textContent = validationUtils.encodeHtmlEntities(applicationId);
            }
        }
    };

    var handleError = function (context, error) {
        if (typeof console !== 'undefined' && console.error) {
            console.error(context + ':', error.message || error);
        }
    };

    return {
        init: function () {
            init();
        },
        goToStep: function (step) {
            goToStep(step);
        },
        showErrorMessage: function (message) {
            showErrorMessage(message);
        },
        showSuccessMessage: function (message, applicationId) {
            showSuccessMessage(message, applicationId);
        }
    };
}();

KTUtil.onDOMContentLoaded(function () {
    ApplicationForm.init();
});
